import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { api } from '../lib/api'
import { clearToken, getToken, isTokenExpired, parseClaims, saveToken } from '../lib/session'
import type { UserClaims } from '../lib/permissions'

interface AuthState {
  claims: UserClaims | null
  loading: boolean
  /** true while we are between password-ok and TOTP verify */
  totpPending: boolean
  challengeToken: string | null
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>
  verifyTotp: (code: string) => Promise<void>
  logout: () => Promise<void>
  refreshClaims: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    claims: null,
    loading: true,
    totpPending: false,
    challengeToken: null,
  })

  const refreshClaims = useCallback(async () => {
    const token = getToken()
    if (!token || isTokenExpired(token)) {
      clearToken()
      setState(s => ({ ...s, claims: null, loading: false }))
      return
    }
    const claims = parseClaims(token)
    setState(s => ({ ...s, claims, loading: false }))
  }, [])

  useEffect(() => { refreshClaims() }, [refreshClaims])

  const login = useCallback(async (email: string, password: string) => {
    const result = await api.login(email, password)
    if (result.requires_totp && result.challenge_token) {
      setState(s => ({
        ...s,
        totpPending: true,
        challengeToken: result.challenge_token!,
        loading: false,
      }))
    } else if (result.access_token) {
      saveToken(result.access_token)
      setState(s => ({
        ...s,
        claims: parseClaims(result.access_token!),
        totpPending: false,
        challengeToken: null,
        loading: false,
      }))
    }
  }, [])

  const verifyTotp = useCallback(async (code: string) => {
    if (!state.challengeToken) throw new Error('No TOTP challenge active')
    const result = await api.totpVerify(state.challengeToken, code)
    saveToken(result.access_token)
    setState(s => ({
      ...s,
      claims: parseClaims(result.access_token),
      totpPending: false,
      challengeToken: null,
    }))
  }, [state.challengeToken])

  const logout = useCallback(async () => {
    await api.logout().catch(() => {})
    clearToken()
    setState({ claims: null, loading: false, totpPending: false, challengeToken: null })
  }, [])

  return (
    <AuthContext.Provider value={{ ...state, login, verifyTotp, logout, refreshClaims }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
